import React, { createContext, useMemo, useState, useEffect, useCallback } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import api, { TOKEN_STORAGE_KEY } from "../api/client";

const THEME_STORAGE_KEY = "rv-theme";
const THEME_SYNC_EVENT = "rv-theme-sync";

// Available theme modes
export const THEME_MODES = {
  light: { label: "Light Mode", paletteMode: "light" },
  dark: { label: "Dark Mode", paletteMode: "dark" },
  greenBlack: { label: "Green/Black", paletteMode: "dark" },
  redBlack: { label: "Red/Black", paletteMode: "dark" },
};

export const ColorModeContext = createContext({
  mode: "light",
  toggleColorMode: () => {},
  setColorMode: () => {},
});

// Theme palette configurations
const getThemePalette = (mode) => {
  switch (mode) {
    case "greenBlack":
      return {
        mode: "dark",
        primary: { main: "#00ff00", contrastText: "#000000" },
        secondary: { main: "#00cc00" },
        background: {
          default: "#000000",
          paper: "#0a0a0a",
        },
        text: {
          primary: "#00ff00",
          secondary: "#00cc00",
        },
      };
    case "redBlack":
      return {
        mode: "dark",
        primary: { main: "#ff3333", contrastText: "#000000" },
        secondary: { main: "#cc0000" },
        background: {
          default: "#000000",
          paper: "#0a0a0a",
        },
        text: {
          primary: "#ff3333",
          secondary: "#cc0000",
        },
      };
    case "dark":
      return {
        mode: "dark",
        primary: { main: "#90caf9" },
        background: {
          default: "#121212",
          paper: "#1d1d1d",
        },
      };
    case "light":
    default:
      return {
        mode: "light",
        primary: { main: "#1e88e5" },
        background: {
          default: "#f7f9fb",
          paper: "#ffffff",
        },
      };
  }
};

export default function ThemeProvider({ children }) {
  const [mode, setMode] = useState("light");

  useEffect(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && THEME_MODES[saved]) setMode(saved);
  }, []);

  useEffect(() => {
    const handleThemeSync = () => {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved && THEME_MODES[saved]) {
        setMode(saved);
      } else {
        setMode("light");
      }
    };

    window.addEventListener(THEME_SYNC_EVENT, handleThemeSync);
    return () => window.removeEventListener(THEME_SYNC_EVENT, handleThemeSync);
  }, []);

  const persistThemePreference = useCallback(async (nextMode) => {
    if (typeof window === "undefined") {
      return;
    }
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      return;
    }
    try {
      await api.put("/users/preferences", { preferredTheme: nextMode });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Unable to persist theme preference", error);
      }
    }
  }, []);

  const updateMode = useCallback(
    (nextMode, options = {}) => {
      const { persist = true, syncStorage = true } = options;
      // Validate mode, default to light if invalid
      const normalized = THEME_MODES[nextMode] ? nextMode : "light";
      setMode(normalized);
      if (typeof window !== "undefined" && syncStorage) {
        window.localStorage.setItem(THEME_STORAGE_KEY, normalized);
        window.dispatchEvent(new Event(THEME_SYNC_EVENT));
      }
      if (persist) {
        persistThemePreference(normalized);
      }
    },
    [persistThemePreference]
  );

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => updateMode(mode === "light" ? "dark" : "light"),
      setColorMode: (value, options) => {
        if (!value) {
          return;
        }
        updateMode(value, options);
      },
    }),
    [mode, updateMode],
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: getThemePalette(mode),
        shape: { borderRadius: 10 },
      }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
}
